<script setup lang="ts">
import SpeckleConnectionChoice from "./SpeckleConnectionChoice.vue";

const emit = defineEmits<{
  chosen: [id: number];
}>();

const handleChosen = (id: number) => {
  emit("chosen", id);
};
</script>

<template>
  <!-- Breaks out of the form column at xl, halved from -mx-40: with the 220px step rail beside the form
       there is only about 92px of slack each side at 1280px, and the wider breakout left these three
       cards visibly cut off on both edges. -->
  <!-- items-start: each card keeps its own height, so expanding one card's "What's included" grows
       that card alone instead of stretching the whole row to the tallest sibling. -->
  <div class="@container grid grid-cols-12 items-start gap-4 xl:gap-8">
    <SpeckleConnectionChoice
      :id="0"
      headline="Without a 3D model"
      description="You enter constructions, materials and quantities yourself, or import them from Excel."
      action-label="Continue without a model"
      :benefits="[
        'Input manual constructions, materials, and quantities',
        'Ability to upload constructions and quantities from Excel',
        'Use simple drag’n’drop to use materials from our Material Database',
        'Create LCA calculations and documentation',
        'Create variance comparisons',
        'Invite users into your project',
        '<i>Communicate and collaborate within the project - coming soon</i>',
      ]"
      data-cy="without-3d-model"
      @chosen="handleChosen">
    </SpeckleConnectionChoice>
    <SpeckleConnectionChoice
      :id="1"
      headline="A new model connection"
      marker="The common choice"
      description="Stream geometry and quantities live from your 3D tools through a new Speckle connection, with the 3D viewer."
      action-label="Connect a new model"
      :benefits="[
        'Everything from <i>Without a 3D model</i>',
        'Connect with your 3D model environments',
        'Enable real-time data streaming into the platform from multiple sources',
        'Enable the 3D viewer in the platform',
        '<i>Ability to send data back to the 3D model - coming soon</i>',
      ]"
      data-cy="new-speckle-connection"
      @chosen="handleChosen" />
    <SpeckleConnectionChoice
      :id="2"
      headline="A shared model connection"
      description="Reuse a connection another project already has, so one model can feed several projects."
      action-label="Reuse an existing connection"
      :benefits="[
        'Everything from <i>A new model connection</i>',
        'Enables the option to send data from 3D models to more than one RTLCA project',
        'Enables you to do variance studies on a project level',
      ]"
      data-cy="existing-stream"
      @chosen="handleChosen">
    </SpeckleConnectionChoice>
  </div>
</template>
